// Vincent (Eric 2026-05-13) — Page de listing des transcriptions par vidéo.
// Liste toutes les vidéos de l'org avec un récap rapide de l'état de leurs
// transcriptions (Whisper / Fireflies / Fusionnée) et un bouton qui mène
// directement à la page détail vidéo avec l'onglet "Transcriptions" actif.

import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import DiarisationBatchButton from "../components/video/DiarisationBatchButton"; // Tache 1292
import { useLang } from "../contexts/LanguageContext";
import {
  FileText, CheckCircle, X as XIcon, MagnifyingGlass, ArrowRight, SpinnerGap,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import { API_BASE as API } from "../lib/apiBase";
import CoverageGapsDashboard from "../components/gaps/CoverageGapsDashboard";

export default function TranscriptionsListPage() {
  const { lang } = useLang();
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | merged | no_merged | no_fireflies
  // Vincent #392 (Eric 2026-06-29 SPEC STRICK) — Tri + filtre par parcours.
  const [sortBy, setSortBy] = useState("name_asc"); // name_asc|name_desc|created_asc|created_desc|updated_asc|updated_desc
  const [pathways, setPathways] = useState([]);
  const [pathwayFilter, setPathwayFilter] = useState("all"); // all | <pathway_id>
  const load = async () => {
    try {
      setLoading(true);
      const [vRes, pRes] = await Promise.all([
        axios.get(`${API}/api/videos/transcripts/overview`, { withCredentials: true }),
        axios.get(`${API}/api/pathways`, { withCredentials: true }).catch(() => ({ data: { pathways: [] } })),
      ]);
      setVideos(Array.isArray(vRes.data?.videos) ? vRes.data.videos : []);
      const pwData = pRes.data?.pathways || pRes.data || [];
      setPathways(Array.isArray(pwData) ? pwData : []);
    } catch (e) {
      console.error("transcripts/overview load failed", e);
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  // Corrige le 2026-06-30 — Reste de l'extraction du tableau de bord
  // (tâches 1256/1278) : `loadGapsDash` vit maintenant DANS
  // `CoverageGapsDashboard`, qui se charge lui-même. L'appel oublié ici
  // faisait planter TOUTE la page Transcriptions (« loadGapsDash is not
  // defined ») — l'écran était inutilisable.
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = (search || "").toLowerCase().trim();
    // Pathway → video_ids map
    let pathwayVideoIds = null;
    if (pathwayFilter !== "all") {
      const p = pathways.find(x => x.id === pathwayFilter);
      pathwayVideoIds = new Set(p?.video_ids || []);
    }
    const arr = videos.filter(v => {
      if (q && !((v.title || "").toLowerCase().includes(q))) return false;
      if (filter === "merged" && !v.has_merged) return false;
      if (filter === "no_merged" && v.has_merged) return false;
      if (filter === "no_fireflies" && v.has_fireflies) return false;
      if (pathwayVideoIds && !pathwayVideoIds.has(v.id)) return false;
      return true;
    });
    // Sort
    const cmpStr = (a, b) => String(a || "").localeCompare(String(b || ""), "fr", { sensitivity: "base" });
    const ts = (d) => (d ? new Date(d).getTime() : 0);
    const sorted = [...arr];
    switch (sortBy) {
      case "name_asc": sorted.sort((a, b) => cmpStr(a.title, b.title)); break;
      case "name_desc": sorted.sort((a, b) => cmpStr(b.title, a.title)); break;
      case "created_asc": sorted.sort((a, b) => ts(a.created_at) - ts(b.created_at)); break;
      case "created_desc": sorted.sort((a, b) => ts(b.created_at) - ts(a.created_at)); break;
      case "updated_asc": sorted.sort((a, b) => ts(a.updated_at) - ts(b.updated_at)); break;
      case "updated_desc": sorted.sort((a, b) => ts(b.updated_at) - ts(a.updated_at)); break;
      default: break;
    }
    return sorted;
  }, [videos, search, filter, sortBy, pathwayFilter, pathways]);

  const openTranscription = (videoId) => {
    navigate(`/dashboard/videos/${videoId}?tab=transcript&source=merged`);
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 flex items-center gap-2">
            <FileText size={24} weight="bold" />
            {lang === "fr" ? "Transcriptions" : "Transcripts"}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {lang === "fr"
              ? "Toutes les vidéos avec leurs transcriptions Whisper, Fireflies et fusionnée. Cliquer ouvre directement le panneau Transcriptions."
              : "All videos with their Whisper, Fireflies and merged transcripts. Click opens the Transcripts panel directly."}
          </p>
        </div>
      </div>

      {/* Tâches 1256 · 1278 — Le tableau de bord vit dans son propre
          composant : il est affiché ici ET sur son écran de menu
          (« Écarts de transcription »), sans duplication de code. */}
      <CoverageGapsDashboard lang={lang} onOpenVideo={openTranscription} />

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center mb-4">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={lang === "fr" ? "Rechercher une vidéo…" : "Search video…"}
            className="w-full pl-9 pr-3 py-2 text-sm border border-zinc-300 rounded-md focus:ring-2 focus:ring-[#002FA7]"
            data-testid="transc-search"
          />
        </div>
        <div className="flex gap-1 text-xs">
          {[
            { id: "all",          label: lang === "fr" ? "Toutes"            : "All" },
            { id: "merged",       label: lang === "fr" ? "Fusion ✓"          : "Merged ✓" },
            { id: "no_merged",    label: lang === "fr" ? "Fusion manquante"  : "No merge" },
            { id: "no_fireflies", label: lang === "fr" ? "Fireflies manquant": "No Fireflies" },
          ].map(o => (
            <button
              key={o.id}
              onClick={() => setFilter(o.id)}
              className={`px-3 py-1.5 rounded-md border ${filter === o.id ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"}`}
              data-testid={`transc-filter-${o.id}`}
            >
              {o.label}
            </button>
          ))}
        </div>
        {/* Tâche 1252 — Export GLOBAL des écarts : corriger une session entière
            sans ouvrir vingt vidéos une par une. */}
        <button
          onClick={() =>
            window.open(
              `${process.env.REACT_APP_BACKEND_URL}/api/videos/coverage-gaps-export?only_alerts=true`,
              "_blank",
            )
          }
          className="ml-auto px-3 py-1.5 text-xs rounded-md border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
          title={lang === "fr"
            ? "Fichier .csv des passages non couverts, toutes vidéos signalées"
            : "CSV of uncovered passages across flagged videos"}
          data-testid="transc-export-gaps"
        >
          ⬇ {lang === "fr" ? "Exporter les écarts" : "Export gaps"}
        </button>
        <button
          onClick={load}
          className="px-3 py-1.5 text-xs rounded-md border border-zinc-300 hover:bg-zinc-50"
          data-testid="transc-refresh"
        >↻ {lang === "fr" ? "Rafraîchir" : "Refresh"}</button>
        {/* Tache 1292 — relance groupee avec le cout annonce AVANT. */}
        <DiarisationBatchButton />
      </div>

      {/* Vincent #392 (Eric 2026-06-29 SPEC STRICK) — Tri + filtre parcours. */}
      <div className="flex flex-wrap gap-3 items-center mb-4 text-xs">
        <label className="inline-flex items-center gap-2 text-zinc-700 font-medium">
          {lang === "fr" ? "Trier par" : "Sort by"} :
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="border border-zinc-300 rounded-md px-2 py-1.5 text-xs bg-white"
            data-testid="transc-sort"
          >
            <option value="name_asc">{lang === "fr" ? "Nom (A → Z)" : "Name (A → Z)"}</option>
            <option value="name_desc">{lang === "fr" ? "Nom (Z → A)" : "Name (Z → A)"}</option>
            <option value="created_asc">{lang === "fr" ? "Date d'ajout (croissante)" : "Date added (asc)"}</option>
            <option value="created_desc">{lang === "fr" ? "Date d'ajout (décroissante)" : "Date added (desc)"}</option>
            <option value="updated_asc">{lang === "fr" ? "Date de modification (croissante)" : "Date modified (asc)"}</option>
            <option value="updated_desc">{lang === "fr" ? "Date de modification (décroissante)" : "Date modified (desc)"}</option>
          </select>
        </label>
        <label className="inline-flex items-center gap-2 text-zinc-700 font-medium">
          {lang === "fr" ? "Parcours" : "Pathway"} :
          <select
            value={pathwayFilter}
            onChange={(e) => setPathwayFilter(e.target.value)}
            className="border border-zinc-300 rounded-md px-2 py-1.5 text-xs bg-white max-w-[280px]"
            data-testid="transc-pathway-filter"
          >
            <option value="all">{lang === "fr" ? "Tous les parcours" : "All pathways"}</option>
            {[...pathways]
              .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "fr", { sensitivity: "base" }))
              .map(p => (
                <option key={p.id} value={p.id}>
                  {p.pathway_type === "onboarding" ? "🎓 " : "📚 "}{p.name}
                </option>
              ))}
          </select>
        </label>
      </div>

      {/* List */}
      {loading ? (
        <div className="py-20 text-center text-zinc-500">
          <SpinnerGap size={28} className="inline animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center text-zinc-500 text-sm">
          {lang === "fr" ? "Aucune transcription trouvée." : "No transcripts found."}
        </div>
      ) : (
        <div className="border border-zinc-200 rounded-lg overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2">{lang === "fr" ? "Vidéo" : "Video"}</th>
                <th className="text-center px-3 py-2 w-[100px]">Whisper</th>
                <th className="text-center px-3 py-2 w-[100px]">Fireflies</th>
                <th className="text-center px-3 py-2 w-[110px]">{lang === "fr" ? "Fusion" : "Merge"}</th>
                <th className="text-center px-3 py-2 w-[140px]">{lang === "fr" ? "Corrections" : "Corrections"}</th>
                <th className="text-right px-3 py-2 w-[160px]"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(v => (
                <tr key={v.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`transc-row-${v.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium text-zinc-900">{v.title || "Sans titre"}</div>
                    {v.created_at && (
                      <div className="text-[10px] text-zinc-400">{String(v.created_at).slice(0, 10)}</div>
                    )}
                  </td>
                  <td className="text-center">
                    {v.has_whisper ? <CheckCircle size={16} className="inline text-teal-600" weight="fill" /> : <XIcon size={14} className="inline text-zinc-300" />}
                  </td>
                  <td className="text-center">
                    {v.has_fireflies ? <CheckCircle size={16} className="inline text-teal-600" weight="fill" /> : <XIcon size={14} className="inline text-zinc-300" />}
                  </td>
                  <td className="text-center">
                    {v.has_merged ? (
                      <CheckCircle size={16} className="inline text-teal-600" weight="fill" />
                    ) : (
                      <span className="text-[10px] text-zinc-400 italic">{lang === "fr" ? "à générer" : "to generate"}</span>
                    )}
                  </td>
                  <td className="text-center">
                    {v.corrections_count > 0 ? (
                      <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-semibold">
                        {v.corrections_count}
                      </span>
                    ) : (
                      <span className="text-zinc-300">—</span>
                    )}
                  </td>
                  <td className="text-right px-3 py-2">
                    <button
                      onClick={() => openTranscription(v.id)}
                      className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-[#002FA7] text-white hover:bg-[#001f7a]"
                      data-testid={`transc-open-${v.id}`}
                    >
                      {lang === "fr" ? "Ouvrir" : "Open"} <ArrowRight size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
